<template>
  <div>
    <context-bar />
    <v-row>
      <v-col cols="12" lg="9">
        <div class="d-flex align-center mb-2">
          <div class="text-subtitle-1">Datasets</div>
          <span class="ml-3 text-caption grey--text">Each dataset is one read-only SQL query. Tables, tiles and charts read from them.</span>
          <v-spacer />
          <v-btn small color="primary" @click="addDataset"><v-icon left small>mdi-plus</v-icon>Add dataset</v-btn>
        </div>

        <v-expansion-panels v-model="open" multiple>
          <v-expansion-panel v-for="(d, i) in datasets" :key="i">
            <v-expansion-panel-header>
              <span><b>{{ d.name }}</b> <span class="grey--text text-caption ml-2">{{ d.description }}</span></span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row dense>
                <v-col cols="12" sm="4"><v-text-field :value="d.name" outlined dense hide-details label="Name" @change="rename(d, $event)" /></v-col>
                <v-col cols="12" sm="6"><v-text-field v-model="d.description" outlined dense hide-details label="Note (for builders)" /></v-col>
                <v-col cols="12" sm="2"><v-text-field v-model.number="d.maxRows" type="number" outlined dense hide-details label="Max rows" placeholder="50000" /></v-col>
              </v-row>
              <div class="text-caption mt-3 mb-1">SQL</div>
              <code-box v-model="d.sql" mode="sql" :rows="8" />
              <div class="mt-2">
                <v-btn x-small text @click="toggleProcessing(d)">
                  {{ d.processing === undefined ? 'Add' : 'Remove' }} processing code (JavaScript)
                </v-btn>
              </div>
              <div v-if="d.processing !== undefined">
                <div class="text-caption mb-1">
                  Runs after the query. Receives <code>rows, params, datasets, helpers</code>; must <code>return</code> an array of rows
                  (helpers: sum, round2, pct, groupBy, sortBy).
                </div>
                <code-box v-model="d.processing" mode="js" :rows="6" />
              </div>
              <div class="mt-3 d-flex align-center">
                <v-btn small color="primary" :loading="busy[i]" @click="preview(d, i)"><v-icon left small>mdi-play</v-icon>Run preview</v-btn>
                <v-btn small text class="ml-2" :disabled="!result[d.name]" @click="useColumns(d)">Use result columns in a new table</v-btn>
                <v-spacer />
                <v-btn small text color="error" @click="datasets.splice(i, 1)"><v-icon left small>mdi-delete-outline</v-icon>Delete</v-btn>
              </div>
              <v-alert v-if="errors[d.name]" type="error" dense text class="mt-2">{{ errors[d.name] }}</v-alert>
              <div v-if="result[d.name]" class="mt-2">
                <div class="text-caption grey--text">
                  {{ result[d.name].rows.length }} row(s) shown{{ result[d.name].truncated ? ' (truncated)' : '' }}; processing code is not applied in this quick preview.
                </div>
                <v-simple-table dense style="max-height: 280px; overflow: auto">
                  <thead><tr><th v-for="c in result[d.name].columns" :key="c">{{ c }}</th></tr></thead>
                  <tbody>
                    <tr v-for="(r, k) in result[d.name].rows.slice(0, 20)" :key="k"><td v-for="c in result[d.name].columns" :key="c">{{ r[c] }}</td></tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-alert v-if="!datasets.length" type="warning" text dense class="mt-2">This part has no datasets yet.</v-alert>

        <!-- values used by the preview -->
        <v-card outlined class="mt-4">
          <v-card-title class="text-subtitle-2">Test filter values for previews</v-card-title>
          <v-card-text>
            <rb-filters v-if="tpl.params.length" v-model="state.previewParams" :params="tpl.params" :options="state.paramOptions" :hide-run="true" />
            <div v-else class="text-caption grey--text">This report has no filters.</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="3">
        <v-card outlined>
          <v-card-title class="text-subtitle-2">SQL rules</v-card-title>
          <v-card-text class="text-caption">
            One <code>SELECT</code> / <code>WITH</code> per dataset. Use <code>:filter</code> names, never schema-qualify tables, and never use
            <code>sealedDocument</code> or password columns. Results are read-only and time-limited.
          </v-card-text>
          <v-divider />
          <v-card-title class="text-subtitle-2">Shared building blocks</v-card-title>
          <v-card-text class="text-caption">
            <div v-for="m in macros" :key="m.name" class="mb-2">
              <code>{{ macroText(m) }}</code>
              <div>{{ m.description }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { errorText } from '../rbUtil'
import axios from 'axios'
import { mapGetters } from 'vuex'
import CodeBox from './CodeBox.vue'
import ContextBar from './ContextBar.vue'
import RbFilters from '../RbFilters.vue'
import { state, container, uniqueName, refreshParamOptions } from './editorState'

export default {
  name: 'RbDataTab',
  components: { CodeBox, ContextBar, RbFilters },
  data () {
    return { open: [0], busy: {}, result: {}, errors: {}, state }
  },
  computed: {
    ...mapGetters(['getEndpoint']),
    tpl () { return state.tpl },
    datasets () { return container().datasets },
    macros () { return (state.meta && state.meta.macroDocs) || [] }
  },
  mounted () {
    refreshParamOptions(this.getEndpoint)
  },
  watch: {
    // different part selected: forget old previews
    'state.ctx' () { this.result = {}; this.errors = {}; this.open = [0] }
  },
  methods: {
    macroText (m) {
      return '{' + '{' + m.name + (m.args ? '(' + m.args + ')' : '') + '}' + '}'
    },
    addDataset () {
      const name = uniqueName('data', this.datasets.map((d) => d.name))
      this.datasets.push({ name, description: '', sql: 'SELECT 1 AS example' })
      this.open = [...this.open, this.datasets.length - 1]
    },
    toggleProcessing (d) {
      if (d.processing === undefined) this.$set(d, 'processing', '// rows, params, datasets, helpers\nreturn rows;')
      else this.$delete(d, 'processing')
    },
    // renaming a dataset also updates everything that points at it
    rename (d, newName) {
      const old = d.name
      if (!newName || newName === old) return
      d.name = newName
      const c = container()
      for (const s of c.ui.sections) {
        if (s.dataset === old) s.dataset = newName
        for (const it of s.items || []) if (it.dataset === old) it.dataset = newName
      }
      for (const e of c.exports || []) if (e.dataset === old) e.dataset = newName
      if (state.ctx === 'main') for (const x of this.tpl.tests.cases || []) if (!x.drill && x.dataset === old) x.dataset = newName
    },
    async preview (d, i) {
      this.$set(this.busy, i, true)
      this.$delete(this.errors, d.name)
      try {
        const res = await axios.post(`${this.getEndpoint}/api/rb-builder/preview-sql`, {
          sql: d.sql, params: this.tpl.params, values: state.previewParams
        })
        this.$set(this.result, d.name, res.data.data)
        this.$set(state.lastColumns, `${state.ctx}.${d.name}`, res.data.data.columns)
      } catch (e) {
        this.$set(this.errors, d.name, errorText(e))
      } finally {
        this.$set(this.busy, i, false)
      }
    },
    useColumns (d) {
      const cols = this.result[d.name].columns
      container().ui.sections.push({
        id: uniqueName('s', container().ui.sections.map((s) => s.id)), type: 'table', title: d.name, width: 12, dataset: d.name,
        columns: cols.map((c) => ({ field: c, label: c }))
      })
      this.$snack('A table with these columns was added in the Layout tab.', 'success')
    }
  }
}
</script>
